import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionSettingsLocationsCard, SubscriptionSettingsSelectionCard } from "../../../../../../stepper/stepperBody/stepperBodyComponents/SelectApiSubscriptionSettings";
import { setApiSubscriptionSettings, disableNext } from '../../../../../../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';
import '../../styles/ApiSubscriptionSettings.scss';

export const ApiSubscriptionSettings = ({ apiDetails, subscriptionData }) => {

  const dispatch = useDispatch();
  const { id: apiId, apiDisplayName } = apiDetails;
  const selectedApiId = apiId.replace('api-v2-', '');
  const [selectedLocation, setSelectedLocation] = useState(null);

  const { entityIdNameMapping, entityIdAddressMapping } = useSelector(
    (state) => state.loginDetailsSlice
  );
  
  const { requestedIntegrationStatuses, requestedApiSubscriptionSettings } = useSelector(
    (state) => state.ManageIntegrationsV2Slice
  );

  const sysInfoState = useSelector((state) => state.ConfigurationsSlice);

  // Get locations that are enabled for this integration
  const availableLocations = useMemo(() => {
    const locations = [];
    Object.keys(requestedIntegrationStatuses).forEach((entityId) => {
      if (requestedIntegrationStatuses[entityId] === 'enable') {
        locations.push({
          entityId,
          location: entityIdNameMapping[entityId],
          address: entityIdAddressMapping[entityId],
        });
      }
    });
    return locations;
  }, [requestedIntegrationStatuses, entityIdNameMapping, entityIdAddressMapping]);

  // Get API preferences configuration from system info
  const apiOptionsMap = sysInfoState.dealerApiPreferences || {};
  const apiOptions = apiOptionsMap[selectedApiId] || [];

  const shouldDisableNext = useMemo(() => {
    if (!availableLocations?.length) {
      return true;
    }
    // Check if all required options are filled for all locations
    for (const location of availableLocations) {
      for (const option of apiOptions) {
        if (option.required) {
          const locationPrefs =
            requestedApiSubscriptionSettings[location.entityId];

          // Check if the specific required option has a valid value
          const optionValue = locationPrefs?.[option.name];
          
          if (optionValue === undefined || optionValue === null) {
            return true;
          }
          
          if (Array.isArray(optionValue) && optionValue.length === 0) {
            return true;
          }
          
          if (typeof optionValue === 'string' && optionValue.trim() === '') {
            return true;
          }
        }
      }
    }

    return false;
  }, [availableLocations, requestedApiSubscriptionSettings, apiOptions]);

  useEffect(() => {
    if (availableLocations.length > 0) {
      setSelectedLocation(availableLocations[0]);
    } else {
      setSelectedLocation(null);
    }
  }, [availableLocations]);

  useEffect(() => {
    dispatch(disableNext(shouldDisableNext));
    
    return () => {
      dispatch(disableNext(false));
    };
  }, [shouldDisableNext]);

  return (
    <div className="mi-api-subscription-settings__container">
      <p className="mi-api-subscription-settings__description">
        Configure the Subscription Settings for API <span className="mi-api-subscription-settings__api-name">{apiDisplayName}</span> at each location.
      </p>
      <div className="mi-api-subscription-settings__cards">
        <SubscriptionSettingsLocationsCard
          locations={availableLocations}
          selectedLocation={selectedLocation}
          selectLocation={(loc) => setSelectedLocation(loc)}
        />

        <SubscriptionSettingsSelectionCard
          apiOptions={apiOptions}
          selectedApi={{ apiId }}
          selectedLocation={selectedLocation}
          setApiSubscriptionSettings={(settings) => {
            dispatch(
              setApiSubscriptionSettings({
                entityId: selectedLocation.entityId,
                settings: settings,
              })
            );
          }}
          selectedDealerApiPreferences={{
            [selectedLocation?.entityId]: {
              [apiId]: requestedApiSubscriptionSettings[selectedLocation?.entityId] || {}
            }
          }}
        />
      </div>
    </div>
  );
}
